/** Single source: path → page title, breadcrumb label, optional description. */

import { normalizeLegacyRoutePath, ROUTES } from "../lib/constants/routes";

export interface RoutePageConfig {
  title: string;
  description?: string;
}

const ROUTE_CONFIG: Record<string, RoutePageConfig> = {
  "/": { title: "Tổng quan" },
  "/ai": { title: "AI" },
  "/ai/chat": { title: "Chat" },
  "/ai/models": { title: "AI Models" },
  "/ai/generate": {
    title: "AI Text Generation",
    description: "Tạo nội dung chất lượng cao dựa trên prompt thực tế và mô hình AI được cấu hình.",
  },
  "/ai/conversation": { title: "Conversation" },
  "/ai/analysis": { title: "AI Analysis" },
  "/ai/code-generator": { title: "Code Generator" },
  "/ai/code-analysis": { title: "Code Analysis" },
  "/ai/nice-travel": { title: "Nice Travel Booking Agent" },
  "/rag/documents": {
    title: "Approved Corpus",
    description: "Legacy alias of Knowledge Approved Corpus inventory.",
  },
  "/rag/documents/:documentId": {
    title: "Document Detail",
    description: "Approved RAG evidence, governance metadata, indexing state, and chunks.",
  },
  "/platform/search": {
    title: "Search",
    description: "Semantic search across approved platform knowledge and service records.",
  },
  "/rag/search": { title: "RAG Search" },
  "/rag/embeddings": {
    title: "Embeddings",
    description: "Model info and vector operations for RAG.",
  },
  "/rag/operations": { title: "Operations" },
  "/rag/evaluation": {
    title: "Kịch bản kiểm định RAG",
    description: "Chạy từng kịch bản hoặc chạy tất cả. Kết quả so với kỳ vọng (status, fields).",
  },
  "/rag/ingest": { title: "Ingest" },
  "/ai/ranking": { title: "Relevance ranking" },
  "/intake": { title: "Intake Core" },
  "/parties": {
    title: "Directory",
    description: "Customer directory: profiles by legal identity state. Open Workspace from a row.",
  },
  "/parties/identity-review": {
    title: "Identity review",
    description: "Unresolved party bindings and identity review. Confirm, create-and-confirm, or rebind with audit.",
  },
  "/parties/suppliers": {
    title: "Suppliers",
    description: "Supplier directory (party_role=supplier) on Commercial Party master.",
  },
  "/parties/suppliers/identity-review": {
    title: "Supplier identity review",
    description: "Identity review queue scoped for supplier parties.",
  },
  "/parties/partners": {
    title: "Partners",
    description: "Partner directory (party_role=partner) on Commercial Party master.",
  },
  "/parties/partners/identity-review": {
    title: "Partner identity review",
    description: "Identity review queue scoped for partner parties.",
  },
  "/dashboard": {
    title: "Overview",
    description: "Intake operations signals with composed Work and Finance cues. Act in Work or Finance modules.",
  },
  "/drafts": {
    title: "Review",
    description: "AI draft review queue across channels. Approve, reject, return, create tasks, or edit and send a reply.",
  },
  "/records": {
    title: "Records",
    description: "Inbound records from connected sources, prepare status, and linked intake artifacts.",
  },
  "/digest": {
    title: "Digest",
    description: "Operational digest, notification summary, and schedule status.",
  },
  "/conversations": {
    title: "Conversations",
    description: "Intake conversation queue: triage, guidance, safety ack, or identity binding. Queues discover work.",
  },
  "/conversations/channel-scope": {
    title: "Channel scope",
    description: "Admit or decline Zalo chats into Conversations. Channel admit and account ACL are Intake-owned.",
  },
  "/ledger": {
    title: "Ledger",
    description: "Audit trail of intake actions and state changes.",
  },
  "/projects": {
    title: "Projects",
    description: "Work orchestration projects, status, SLA signals, and portfolio filters.",
  },
  "/tasks": {
    title: "Tasks",
    description: "Work items linked to intake threads and projects.",
  },
  "/custom-view": {
    title: "Custom View",
    description: "Operator workspace for cross-surface work monitoring.",
  },
  "/operate/analytics": {
    title: "Analytics",
    description: "Workload, quality, productivity metrics, and exportable Work reports.",
  },
  "/strategy": {
    title: "Strategy & Goals",
    description: "Executive OKR/MBO dashboard for active cycles, objectives, progress, and reviews.",
  },
  "/strategy/goals": {
    title: "Goals",
    description: "Objectives, key results, manual check-ins, and execution links.",
  },
  "/strategy/alignment": {
    title: "Alignment",
    description: "Goal cascade, contributions, orphan goals, and cross-team blockers.",
  },
  "/strategy/reviews": {
    title: "Reviews",
    description: "Weekly check-ins and quarterly review packs.",
  },
  "/settings/me": {
    title: "Account",
    description: "User Workspace account shell.",
  },
  "/settings/me/profile": {
    title: "Tài khoản",
    description: "User Workspace profile and personal platform preferences.",
  },
  "/settings/me/mail": {
    title: "Cài đặt thư",
    description: "Mailbox, OAuth, and outbound email configuration.",
  },
  "/settings/me/mail/oauth-close": {
    title: "Mail OAuth",
    description: "OAuth popup close handshake for personal mailbox connect.",
  },
  "/settings/me/password": {
    title: "Mật khẩu",
    description: "Change account password.",
  },
  "/settings/me/ai-defaults": {
    title: "Mặc định AI",
    description: "Default AI prompt template selection and assistant behavior.",
  },
  "/settings/me/zalo": {
    title: "Kết nối Zalo",
    description: "Personal Zalo authorization and connection status.",
  },
  "/prompt-templates": {
    title: "Draft templates",
    description: "Draft templates; choose your default in Account settings.",
  },
  "/triage-templates": {
    title: "Triage templates",
    description: "Triage classification templates.",
  },
  "/thread-digest-templates": {
    title: "Thread digest templates",
    description: "Thread digest templates.",
  },
  "/request-info-templates": {
    title: "Request information templates",
    description: "Request-info templates.",
  },
  "/ai-audit": {
    title: "AI Audit",
    description: "Investigate AI calls, prompt provenance, raw payload access, and cross-request evidence trails.",
  },
  "/llm-privacy": {
    title: "AI Privacy Rules",
    description: "AI privacy rules, verification, and published masking contract.",
  },
  "/rules/llm-privacy": {
    title: "AI Privacy Rules",
    description: "AI privacy rules, verification, and published masking contract.",
  },
  "/rules/template-routing-rules": {
    title: "Template Routing Rules",
    description: "Rules that choose prompt templates by sender/client context.",
  },
  "/rules": {
    title: "Policy Rules",
    description: "Governance policy rules and operational routing contracts.",
  },
  "/admin": {
    title: "Platform Administration",
    description: "Platform operations, integrations, sync controls, and governance shortcuts.",
  },
  "/admin/operational": {
    title: "Operational Settings",
    description: "Operational routing, automation, and workflow settings.",
  },
  "/admin/integrations": {
    title: "Integrations",
    description: "SharePoint, notification, and platform connectivity.",
  },
  "/mailbox": {
    title: "Mailbox",
    description: "Organization mailbox connection and sync.",
  },
  "/admin/sync-diagnostics": {
    title: "Sync Diagnostics",
    description: "Runtime sync status and platform dependency health.",
  },
  "/knowledge/corpus": {
    title: "Approved Corpus",
    description: "",
  },
  "/knowledge/review": {
    title: "Pre-RAG Review",
    description: "",
  },
  "/knowledge": {
    title: "Knowledge",
    description: "Capture, Pre-RAG Review, Approved Corpus, and governed Search.",
  },
  "/knowledge/ingest": {
    title: "Ingest Sources",
    description: "URL, crawl, and source ingest into the RAG corpus.",
  },
  "/knowledge/content": {
    title: "Content",
    description: "Content pipeline KPIs, storage health, and quick actions.",
  },
  "/knowledge/content/capture": {
    title: "Capture",
    description: "Canonical content capture before review and RAG publish.",
  },
  "/knowledge/content/review": {
    title: "Duyệt",
    description: "Content review queue before RAG derivative publish.",
  },
  "/knowledge/ops": {
    title: "Index",
    description: "RAG index runtime statistics and operations.",
  },
  "/knowledge/search-lab": {
    title: "Diagnostics",
    description: "Semantic index probe and diagnostics against the RAG index.",
  },
  "/knowledge/embeddings": {
    title: "Embeddings",
    description: "Model info and vector operations for RAG.",
  },
  "/knowledge/evaluation": {
    title: "Evaluation",
    description: "Run acceptance scenarios against RAG gateway endpoints.",
  },
  "/knowledge/content/library": {
    title: "Library",
    description: "Approved content library and published derivatives.",
  },
  "/knowledge/content/library/:itemId": {
    title: "Content detail",
    description: "Full-width content item review with extraction and storage refs.",
  },
  "/knowledge/content/storage": {
    title: "Storage",
    description: "Content storage configuration and blob refs.",
  },
  "/governance/knowledge": {
    title: "Data Governance",
    description: "Enterprise data catalog, knowledge policy, drift, and projection control.",
  },
  "/governance/knowledge/catalog": {
    title: "Catalog",
    description: "Fleet data groups and entity types declared in service knowledge contracts.",
  },
  "/governance/knowledge/enablement": {
    title: "Policy",
    description: "Tenant and package knowledge policy enablement matrix.",
  },
  "/governance/knowledge/drift": {
    title: "Drift",
    description: "Catalog drift versus RAG index and projection ledger.",
  },
  "/governance/knowledge/ops": {
    title: "Projection",
    description: "Projection control, reconcile replay, and outbox/DLQ visibility.",
  },
  "/data-governance": {
    title: "Data Governance",
    description: "Catalog, policy, drift, and projection control.",
  },
  "/data-governance/catalog": {
    title: "Catalog",
    description: "Fleet data groups and entity types declared in service knowledge contracts.",
  },
  "/data-governance/policy": {
    title: "Policy",
    description: "Tenant and package knowledge policy enablement matrix.",
  },
  "/data-governance/drift": {
    title: "Drift",
    description: "Catalog drift versus RAG index and projection ledger.",
  },
  "/data-governance/projection": {
    title: "Projection",
    description: "Projection control, reconcile replay, and outbox/DLQ visibility.",
  },
  "/contracts": {
    title: "Contracts",
    description: "Agreements and lifecycle.",
  },
  "/contracts/templates": {
    title: "Contract Templates",
    description: "Template catalog.",
  },
  "/finance": {
    title: "Tài chính",
    description: "Enterprise receivables, collection records, and billing profile.",
  },
  "/finance/invoices": {
    title: "Phải thu",
    description: "Tenant invoices, collection records, and payment status.",
  },
  "/finance/payables": {
    title: "Phải trả",
    description: "Supplier payables and disbursement requests before bank settlement.",
  },
  "/finance/billing": {
    title: "Hồ sơ thanh toán",
    description: "Payment dossier catalog: capture, DNTT layout, and receiving bank by currency.",
  },
  "/finance/payment-dossiers": {
    title: "Hồ sơ thanh toán",
    description: "Redirects to /finance/billing (payment dossier catalog).",
  },
  "/finance/bank": {
    title: "Sao kê ngân hàng",
    description: "Bank accounts, statement imports, allocation, and exception controls.",
  },
  "/finance/bank/transactions": {
    title: "Giao dịch ngân hàng",
    description: "Cross-account bank transaction workspace.",
  },
  "/finance/reconciliation": {
    title: "Đối soát",
    description: "Period reconciliation runs and summaries.",
  },
  "/finance/reporting": {
    title: "Báo cáo",
    description: "AR aging, transaction history, and cash flow.",
  },
  "/finance/content-review": {
    title: "Duyệt",
    description: "Redirects to Content review queue.",
  },
  "/hrm": {
    title: "Nhân sự",
    description: "Org master, headcount, and employee inventory.",
  },
  "/hrm/org": {
    title: "Tổ chức",
    description: "HRM organization master pillar.",
  },
  "/hrm/hr": {
    title: "Nhân sự",
    description: "HRM HR operations pillar.",
  },
  "/hrm/portal": {
    title: "Cổng nhân viên",
    description: "HRM employee portal pillar.",
  },
  "/hrm/integrations": {
    title: "Tích hợp HRM",
    description: "HRM integrations pillar.",
  },
  "/hrm/portal/leave-request-approval-steps": {
    title: "Bước duyệt nghỉ",
    description: "Leave request approval step catalog.",
  },
  "/hrm/org/legal-entities": {
    title: "Pháp nhân",
    description: "HRM legal entities (org master).",
  },
  "/hrm/org/company-levels": {
    title: "Cấp công ty",
    description: "HRM company level catalog.",
  },
  "/hrm/org/business-lines": {
    title: "Lĩnh vực KD",
    description: "HRM business line catalog.",
  },
  "/hrm/org/employee-groups": {
    title: "Nhóm NV",
    description: "HRM employee group catalog.",
  },
  "/hrm/org/specialties": {
    title: "Chuyên khoa",
    description: "HRM specialty catalog.",
  },
  "/hrm/org/title-groups": {
    title: "Nhóm chức vụ",
    description: "HRM title group catalog.",
  },
  "/hrm/org/job-families": {
    title: "Họ công việc",
    description: "HRM job family catalog.",
  },
  "/hrm/org/job-groups": {
    title: "Nhóm công việc",
    description: "HRM job group catalog.",
  },
  "/hrm/org/org-levels": {
    title: "Cấp phòng ban",
    description: "HRM org level catalog.",
  },
  "/hrm/org/titles": {
    title: "Chức vụ",
    description: "HRM title catalog.",
  },
  "/hrm/org/jobs": {
    title: "Công việc",
    description: "HRM job catalog.",
  },
  "/hrm/org/org-units": {
    title: "Phòng ban",
    description: "HRM org unit tree.",
  },
  "/hrm/org/positions": {
    title: "Vị trí",
    description: "HRM positions catalog.",
  },
  "/hrm/org/headcounts": {
    title: "Định biên",
    description: "HRM headcount and live projections.",
  },
  "/hrm/hr/nationalities": {
    title: "Quốc tịch",
    description: "HRM nationality catalog.",
  },
  "/hrm/hr/ethnicities": {
    title: "Dân tộc",
    description: "HRM ethnicity catalog.",
  },
  "/hrm/hr/banks": {
    title: "Ngân hàng",
    description: "HRM bank catalog.",
  },
  "/hrm/hr/education-levels": {
    title: "Trình độ HV",
    description: "HRM education level catalog.",
  },
  "/hrm/hr/professional-titles": {
    title: "Chức danh CM",
    description: "HRM professional title catalog.",
  },
  "/hrm/hr/employee-types": {
    title: "Loại NV",
    description: "HRM employee type catalog.",
  },
  "/hrm/hr/standard-workdays": {
    title: "Ngày công chuẩn",
    description: "HRM standard workday catalog.",
  },
  "/hrm/hr/attendance-objects": {
    title: "Đối tượng CC",
    description: "HRM attendance object catalog.",
  },
  "/hrm/hr/attendance-work-types": {
    title: "Loại công",
    description: "HRM M04-F002 attendance work type catalog (loại công).",
  },
  "/hrm/hr/attendance-symbols": {
    title: "Ký hiệu chấm công",
    description: "HRM M04-F004 attendance symbol catalog (ký hiệu chấm công).",
  },
  "/hrm/hr/attendance-holidays": {
    title: "Ngày Lễ/Tết",
    description: "HRM M04-F005 holiday / Tết calendar (ngày Lễ/Tết).",
  },
  "/hrm/hr/attendance-shifts": {
    title: "Ca làm việc",
    description: "HRM M04-F006 attendance shift catalog (ca làm việc).",
  },
  "/hrm/hr/attendance-shift-schedules": {
    title: "Lịch ca làm việc",
    description: "HRM M04-F007 shift schedule assignment (lịch ca làm việc).",
  },
  "/hrm/hr/ot-time-windows": {
    title: "Khung giờ OT theo buổi",
    description: "HRM M04-F014 OT time window by session (khung giờ OT theo buổi).",
  },
  "/hrm/hr/ot-reasons": {
    title: "Lý do làm thêm giờ",
    description: "HRM M04 OT overtime reason catalog (lý do làm thêm giờ).",
  },
  "/hrm/hr/ot-plans": {
    title: "Kế hoạch OT",
    description: "HRM M04-F017 OT plan header (kế hoạch làm thêm giờ).",
  },
  "/hrm/hr/ot-plan-lines": {
    title: "Nhân viên trên kế hoạch OT",
    description: "HRM M04-F017 OT plan employees (nhân viên trên kế hoạch OT).",
  },
  "/hrm/hr/collective-business-trips": {
    title: "Công tác tập thể",
    description: "HRM M04-F018 collective business trip batch (đợt công tác tập thể).",
  },
  "/hrm/hr/collective-business-trip-lines": {
    title: "Dòng công tác tập thể",
    description: "HRM M04-F018 collective business trip employees (dòng công tác tập thể).",
  },
  "/hrm/hr/title-policies": {
    title: "Chế độ chính sách theo chức vụ",
    description: "HRM M02-F069 title policy catalog (chế độ chính sách theo chức vụ).",
  },
  "/hrm/hr/birthday-plans": {
    title: "Kế hoạch sinh nhật nhân viên",
    description: "HRM M02-F067 employee birthday plans (kế hoạch sinh nhật nhân viên).",
  },
  "/hrm/hr/key-birthday-plans": {
    title: "Kế hoạch sinh nhật Key",
    description: "HRM M02-F068 key birthday plans (kế hoạch sinh nhật Key).",
  },
  "/hrm/hr/religions": {
    title: "Tôn giáo",
    description: "HRM religion catalog.",
  },
  "/hrm/hr/marital-statuses": {
    title: "Tình trạng hôn nhân",
    description: "HRM marital status catalog.",
  },
  "/hrm/hr/healthcare-places": {
    title: "Nơi khám chữa bệnh",
    description: "HRM healthcare place catalog.",
  },
  "/hrm/hr/training-schools": {
    title: "Trường đào tạo",
    description: "HRM training school catalog.",
  },
  "/hrm/hr/study-majors": {
    title: "Chuyên ngành",
    description: "HRM study major catalog.",
  },
  "/hrm/hr/practice-forms": {
    title: "Hình thức thực hành",
    description: "HRM practice form catalog.",
  },
  "/hrm/hr/practice-durations": {
    title: "Thời gian thực hành",
    description: "HRM practice duration catalog.",
  },
  "/hrm/hr/doctor-competencies": {
    title: "Năng lực bác sĩ",
    description: "HRM doctor competency catalog.",
  },
  "/hrm/hr/occupations": {
    title: "Nghề nghiệp",
    description: "HRM occupations catalog.",
  },
  "/hrm/hr/hospitals": {
    title: "Bệnh viện",
    description: "HRM hospitals catalog.",
  },
  "/hrm/hr/health-statuses": {
    title: "Tình trạng sức khỏe",
    description: "HRM health statuses catalog.",
  },
  "/hrm/hr/health-exam-classifications": {
    title: "Phân loại khám sức khỏe",
    description: "HRM health exam classifications catalog.",
  },
  "/hrm/hr/occupational-diseases": {
    title: "Bệnh nghề nghiệp",
    description: "HRM occupational diseases catalog.",
  },
  "/hrm/hr/foreign-language-levels": {
    title: "Trình độ ngoại ngữ",
    description: "HRM foreign language levels catalog.",
  },
  "/hrm/hr/computer-skill-levels": {
    title: "Trình độ tin học",
    description: "HRM computer skill levels catalog.",
  },
  "/hrm/hr/work-locations": {
    title: "Địa điểm làm việc",
    description: "HRM work locations catalog.",
  },
  "/hrm/hr/business-trip-reasons": {
    title: "Lý do công tác",
    description: "HRM business trip reasons catalog.",
  },
  "/hrm/hr/contract-groups": {
    title: "Nhóm hợp đồng",
    description: "HRM contract groups catalog.",
  },
  "/hrm/hr/contract-types": {
    title: "Loại hợp đồng",
    description: "HRM contract types catalog.",
  },
  "/hrm/hr/constitution-bases": {
    title: "Căn cứ hiến pháp",
    description: "HRM constitution bases catalog.",
  },
  "/hrm/hr/decision-bases": {
    title: "Căn cứ quyết định",
    description: "HRM decision bases catalog.",
  },
  "/hrm/hr/decision-types": {
    title: "Loại quyết định",
    description: "HRM decision types catalog.",
  },
  "/hrm/hr/establishment-reasons": {
    title: "Lý do thành lập",
    description: "HRM establishment reasons catalog.",
  },
  "/hrm/hr/establishment-decisions": {
    title: "Lập quyết định",
    description: "HRM establishment decisions process.",
  },
  "/hrm/hr/directive-bases": {
    title: "Căn cứ chỉ đạo",
    description: "HRM directive bases catalog.",
  },
  "/hrm/hr/discipline-forms": {
    title: "Hình thức kỷ luật",
    description: "HRM discipline forms catalog.",
  },
  "/hrm/hr/resignation-reasons": {
    title: "Lý do nghỉ việc",
    description: "HRM resignation reasons catalog.",
  },
  "/hrm/hr/reward-forms": {
    title: "Hình thức khen thưởng",
    description: "HRM reward forms catalog.",
  },
  "/hrm/hr/training-classifications": {
    title: "Phân loại đào tạo",
    description: "HRM training classifications catalog.",
  },
  "/hrm/hr/training-systems": {
    title: "Hệ đào tạo",
    description: "HRM training systems catalog.",
  },
  "/hrm/hr/clinical-specialties": {
    title: "Chuyên khoa lâm sàng",
    description: "HRM clinical specialties catalog.",
  },
  "/hrm/hr/specialty-disease-classifications": {
    title: "Phân loại bệnh chuyên khoa",
    description: "HRM specialty disease classifications catalog.",
  },
  "/hrm/hr/salary-change-reasons": {
    title: "Lý do đổi lương",
    description: "HRM salary change reasons catalog.",
  },
  "/hrm/hr/position-change-reasons": {
    title: "Lý do đổi vị trí",
    description: "HRM position change reasons catalog.",
  },
  "/hrm/hr/technical-specialties": {
    title: "Danh mục kỹ thuật",
    description: "HRM technical specialties catalog.",
  },
  "/hrm/hr/dmkt-registration-campaigns": {
    title: "Đợt đăng ký DMKT",
    description: "HRM DMKT registration campaign catalog.",
  },
  "/hrm/hr/certificates": {
    title: "Chứng chỉ",
    description: "HRM certificates catalog.",
  },
  "/hrm/hr/diplomas": {
    title: "Bằng cấp",
    description: "HRM diplomas catalog.",
  },
  "/hrm/hr/health-records": {
    title: "Ghi nhận sức khỏe",
    description: "HRM health records catalog.",
  },
  "/hrm/hr/procedure-dossiers": {
    title: "Hồ sơ thủ tục",
    description: "HRM procedure dossiers catalog.",
  },
  "/hrm/hr/evaluation-records": {
    title: "Hồ sơ đánh giá",
    description: "HRM evaluation records.",
  },
  "/hrm/hr/onboarding-records": {
    title: "Hồ sơ tiếp nhận",
    description: "HRM onboarding records.",
  },
  "/hrm/hr/job-description-records": {
    title: "Hồ sơ mô tả việc",
    description: "HRM job description records.",
  },
  "/hrm/hr/training-histories": {
    title: "Quá trình đào tạo",
    description: "HRM training histories.",
  },
  "/hrm/hr/contracts": {
    title: "Hợp đồng",
    description: "HRM labor contracts.",
  },
  "/hrm/hr/work-histories": {
    title: "Quá trình làm việc",
    description: "HRM work histories.",
  },
  "/hrm/hr/salary-decisions": {
    title: "Diễn biến lương",
    description: "HRM salary decisions.",
  },
  "/hrm/hr/family-relations": {
    title: "Quan hệ nhân thân",
    description: "HRM family relations.",
  },
  "/hrm/hr/leave-records": {
    title: "Nghỉ phép",
    description: "HRM leave records.",
  },
  "/hrm/hr/discipline-histories": {
    title: "Kỷ luật",
    description: "HRM discipline histories.",
  },
  "/hrm/hr/reward-histories": {
    title: "Khen thưởng",
    description: "HRM reward histories.",
  },
  "/hrm/hr/business-trips": {
    title: "Công tác",
    description: "HRM business trips.",
  },
  "/hrm/hr/maternity-histories": {
    title: "Thai sản",
    description: "HRM maternity histories.",
  },
  "/hrm/hr/concurrent-roles": {
    title: "Kiêm nhiệm",
    description: "HRM concurrent roles.",
  },
  "/hrm/hr/social-insurance-histories": {
    title: "Bảo hiểm xã hội",
    description: "HRM social insurance histories.",
  },
  "/hrm/hr/end-concurrent-roles": {
    title: "Kết thúc kiêm nhiệm",
    description: "HRM end concurrent role decisions.",
  },
  "/hrm/hr/late-early-regimes": {
    title: "Chế độ đi muộn/về sớm",
    description: "HRM late/early attendance regimes.",
  },
  "/hrm/hr/supplemental-leave-grants": {
    title: "Bổ sung phép năm",
    description: "HRM supplemental leave grant records.",
  },
  "/hrm/hr/leave-year-balances": {
    title: "Số dư phép năm",
    description: "HRM annual leave year ledger (carryover + granted).",
  },
  "/hrm/hr/compensatory-leave-grants": {
    title: "Bù phép năm",
    description: "HRM compensatory leave grant records.",
  },
  "/hrm/hr/practice-certificates": {
    title: "Chứng chỉ hành nghề",
    description: "HRM practice certificate records.",
  },
  "/hrm/hr/periodic-health-exams": {
    title: "Khám sức khỏe định kỳ",
    description: "HRM periodic health exam records.",
  },
  "/hrm/hr/hepatitis-b-vaccinations": {
    title: "Tiêm viêm gan B",
    description: "HRM hepatitis B vaccination records.",
  },
  "/hrm/hr/cooperations": {
    title: "Hợp tác",
    description: "HRM cooperation records.",
  },
  "/hrm/hr/work-accidents": {
    title: "Tai nạn lao động",
    description: "HRM work accident records.",
  },
  "/hrm/hr/employee-files": {
    title: "Hồ sơ nhân viên",
    description: "HRM employee file records.",
  },
  "/hrm/hr/occupational-disease-cases": {
    title: "Bệnh nghề nghiệp (hồ sơ)",
    description: "HRM occupational disease case records.",
  },
  "/hrm/hr/practice-mentorships": {
    title: "Hướng dẫn thực hành",
    description: "HRM practice mentorship records.",
  },
  "/hrm/hr/practice-guidance-active": {
    title: "NV đang hướng dẫn thực hành",
    description: "HRM read-only roster of mentors currently guiding practice.",
  },
  "/hrm/hr/practice-mentor-eligibility": {
    title: "Đủ thời gian hành nghề hướng dẫn",
    description: "HRM read-only roster of employees eligible to mentor by practice years.",
  },
  "/hrm/hr/salary-proposals": {
    title: "Đề nghị lương",
    description: "HRM salary proposal list with effective income enrichment.",
  },
  "/hrm/hr/technical-competencies": {
    title: "Năng lực kỹ thuật",
    description: "HRM technical competency records.",
  },
  "/hrm/hr/leave-carryover-extensions": {
    title: "Gia hạn phép tồn",
    description: "HRM leave carryover extension records.",
  },
  "/hrm/hr/regional-minimum-wages": {
    title: "Lương tối thiểu vùng",
    description: "HRM regional minimum wage catalog.",
  },
  "/hrm/hr/si-participation-rates": {
    title: "Tỷ lệ tham gia BH",
    description: "HRM social insurance participation rate catalog.",
  },
  "/hrm/hr/attendance-periods": {
    title: "Kỳ chấm công",
    description: "HRM attendance period lock control.",
  },
  "/hrm/hr/payroll-periods": {
    title: "Kỳ lương",
    description: "HRM payroll period lock control.",
  },
  "/hrm/hr/payroll-item-groups": {
    title: "Nhóm mục lương",
    description: "HRM payroll item group catalog.",
  },
  "/hrm/hr/payroll-items": {
    title: "Mục lương",
    description: "HRM payroll item catalog.",
  },
  "/hrm/hr/payroll-item-formulas": {
    title: "Công thức mục lương",
    description: "HRM payroll item formula catalog.",
  },
  "/hrm/hr/si-monthly-contributions": {
    title: "Trích nộp BHXH hằng tháng",
    description: "HRM monthly SI contribution table generate and list.",
  },
  "/hrm/hr/external-income-individuals": {
    title: "Thu nhập ngoài lương cá nhân",
    description: "HRM external income entries for external collaborators.",
  },
  "/hrm/hr/external-income-blocks": {
    title: "Thu nhập ngoài lương theo Khối",
    description: "HRM external income entries by organization block.",
  },
  "/hrm/hr/salary-plan-entries": {
    title: "Kế hoạch lương Fix/P4P",
    description: "HRM fixed and P4P salary plan catalog.",
  },
  "/hrm/hr/classified-revenues": {
    title: "Doanh thu theo phân loại",
    description: "HRM classified revenue plan and actual catalog.",
  },
  "/hrm/hr/ky1-benefit-rates": {
    title: "Tỉ lệ % Kỳ 1",
    description: "HRM Kỳ 1 benefit percentage rate catalog.",
  },
  "/hrm/hr/hospital-monthly-revenues": {
    title: "Doanh thu BV hàng tháng",
    description: "HRM hospital monthly revenue statistics catalog.",
  },
  "/hrm/hr/duty-conversion-entries": {
    title: "Quỹ ra trực quy đổi",
    description: "HRM duty conversion fund catalog.",
  },
  "/hrm/hr/timesheet-daily-entries": {
    title: "Chấm công ngày (nhập tay)",
    description: "HRM manual timesheet daily catalog.",
  },
  "/hrm/hr/late-early-violations": {
    title: "Vi phạm đi muộn/về sớm",
    description: "HRM late/early >60 minutes list.",
  },
  "/hrm/hr/duty-fund-ledgers": {
    title: "Sổ quỹ ra trực",
    description: "HRM duty fund ledger projection.",
  },
  "/hrm/hr/comp-leave-fund-ledgers": {
    title: "Sổ quỹ nghỉ bù",
    description: "HRM compensatory leave fund ledger.",
  },
  "/hrm/hr/fund-opening-balances": {
    title: "Số dư đầu quỹ",
    description: "HRM fund opening balance seed.",
  },
  "/hrm/hr/payroll-snapshots": {
    title: "Bảng lương kỳ",
    description: "HRM payroll snapshot PH1 records.",
  },
  "/hrm/portal/leave-requests": {
    title: "Đơn nghỉ phép",
    description: "HRM portal leave request workflow.",
  },
  "/hrm/portal/business-trip-requests": {
    title: "Đơn công tác",
    description: "HRM portal business trip request workflow.",
  },
  "/hrm/portal/ot-requests": {
    title: "Đơn làm thêm",
    description: "HRM portal overtime request workflow.",
  },
  "/hrm/portal/ot-request-approval-steps": {
    title: "Bước duyệt OT",
    description: "HRM portal OT request approval steps (read-only).",
  },
  "/hrm/portal/ot-request-edit-events": {
    title: "Lịch sử sửa OT",
    description: "HRM portal OT request edit events (read-only).",
  },
  "/hrm/portal/request-attachments": {
    title: "File đính kèm đơn",
    description: "HRM portal request file attachments (content_ref).",
  },
  "/hrm/portal/leave-balances": {
    title: "Phép năm còn lại",
    description: "HRM portal remaining annual leave projection.",
  },
  "/hrm/portal/timesheet-daily-entries": {
    title: "Bảng công của tôi",
    description: "HRM portal timesheet daily entries (read-only).",
  },
  "/hrm/portal/attendance-explanation-requests": {
    title: "Giải trình công",
    description: "HRM portal attendance explanation request workflow.",
  },
  "/hrm/portal/dmkt-registration-requests": {
    title: "Đăng ký DMKT",
    description: "HRM portal DMKT registration request workflow.",
  },
  "/hrm/portal/dmkt-registration-lines": {
    title: "Dòng đăng ký DMKT",
    description: "HRM portal DMKT registration line catalog.",
  },
  "/hrm/portal/dmkt-registration-approval-steps": {
    title: "Bước duyệt DMKT",
    description: "HRM portal DMKT registration approval steps (read-only).",
  },
  "/hrm/portal/dmkt-registration-edit-events": {
    title: "Lịch sử sửa DMKT",
    description: "HRM portal DMKT registration edit events (read-only).",
  },
  "/hrm/portal/resignation-requests": {
    title: "Đơn nghỉ việc",
    description: "HRM portal resignation request workflow.",
  },
  "/hrm/hr/alerts": {
    title: "Cảnh báo",
    description: "HRM alert catalog (read-only).",
  },
  "/hrm/hr/form-layouts": {
    title: "Biểu mẫu (chỉ schema)",
    description: "HRM danh mục biểu mẫu chỉ schema (đọc).",
  },
  "/hrm/hr/report-layouts": {
    title: "Báo cáo (chỉ schema)",
    description: "HRM danh mục báo cáo chỉ schema (đọc).",
  },
  "/hrm/hr/abr-phase": {
    title: "Chính sách phase ABR",
    description: "HRM chính sách phase ABR chỉ schema (đọc).",
  },
  "/hrm/integrations/ad-ldap": {
    title: "Cấu hình AD/LDAP",
    description: "HRM AD/LDAP auth mode config snapshot (read-only).",
  },
  "/hrm/integrations/webhooks": {
    title: "Giao webhook",
    description: "HRM webhook delivery inventory (read-only).",
  },
  "/hrm/integrations/his-revenue": {
    title: "Doanh thu HIS tháng",
    description: "HRM HIS revenue monthly inventory (read-only).",
  },
  "/hrm/integrations/nfr-security": {
    title: "Bảo mật NFR",
    description: "HRM NFR security measurable snapshot (read-only).",
  },
  "/hrm/hr/employees": {
    title: "Nhân viên",
    description: "HRM employee inventory.",
  },
  "/content/review": {
    title: "Review",
    description: "Queue-only workflow for official documents awaiting review, approval, RAG publish, and Drive folder effects.",
  },
  "/content": {
    title: "Content overview",
    description: "Operational hub for active storage, pending review, recent uploads, and Content Plane quick actions.",
  },
  "/content/capture": {
    title: "Capture",
    description: "Guided upload workflow: intent, active storage destination, upload, extraction result, and next action.",
  },
  "/content/library": {
    title: "Library",
    description: "Browse/search captured content, folder tree, memory items, metadata, content refs, and Drive links.",
  },
  "/content/storage": {
    title: "Storage",
    description: "Single active storage provider with fail-closed readiness; Google Drive Connect/Verify/Sync only when Drive is selected.",
  },
  "/ai/runtime": {
    title: "Workflow AI Policy",
    description: "Workflow model policy, budgets, privacy, audit, and read-only AI Platform catalog snapshot.",
  },
  "/admin/rules": {
    title: "Policy Rules",
    description: "Governance policy rules and operational routing contracts.",
  },
  "/admin/template-routing-rules": {
    title: "Template Routing Rules",
    description: "Rules that choose prompt templates by sender/client context.",
  },
  "/admin/ledger": {
    title: "Admin Ledger",
    description: "Admin ledger options, entries, and audit references.",
  },
  "/user-guide": {
    title: "User guide",
    description: "Platform operator guide shell.",
  },
  "/user-guide/guide": {
    title: "Guide",
    description: "Operator how-to guide.",
  },
  "/user-guide/admin-checklist": {
    title: "Admin checklist",
    description: "Admin acceptance checklist.",
  },
  "/user-guide/tech-spec": {
    title: "Tech spec",
    description: "Technical specification notes.",
  },
  "/iam": {
    title: "IAM Authority",
    description: "Gateway-owned identity, RBAC, roles, permissions, and service API registry authority.",
  },
  "/iam/tenants": {
    title: "Tenants",
    description: "Gateway-owned tenant hierarchy, package assignment, and role-template view.",
  },
  "/iam/services": {
    title: "Services",
    description: "Gateway-owned service registry, contract source, and enforcement mode.",
  },
  "/iam/service-items": {
    title: "Service Items",
    description: "Gateway-owned route permission registry imported from service contracts.",
  },
  "/iam/sync": {
    title: "Sync",
    description: "Gateway-owned microservice route contract import and reconciliation diagnostics.",
  },
  "/iam/audit": {
    title: "Audit",
    description: "Gateway-native IAM audit trail for mutations and enforcement denials.",
  },
  "/iam/simulator": {
    title: "Simulator",
    description: "Route-level policy simulator for service registry access decisions.",
  },
  "/iam/roles": {
    title: "Roles",
    description: "IAM roles, permission contracts, and service API registry assignments.",
  },
  "/iam/users": {
    title: "Users",
    description: "IAM users, role assignment, and Keycloak-first access mapping.",
  },
  "/users": { title: "User Management" },
  "/analytics": { title: "Platform Analytics" },
  "/analytics/dashboard": { title: "Platform Analytics" },
  "/database": { title: "Database Management" },
  "/database/management": { title: "Database Management" },
  "/settings": { title: "Settings" },
  "/monitoring": {
    title: "System Monitoring",
    description: "Real-time overview of system performance, service availability, and infrastructure health.",
  },
  "/monitoring/system": { title: "System Monitoring" },
  "/services": { title: "Services" },
  "/deployment": { title: "Deployment" },
  "/performance": { title: "Performance" },
  "/embed/chat": { title: "Embed Chat" },
  "/login": { title: "Sign in" },
  "/callback": { title: "Authenticating" },
  "/features": { title: "Features" },
  "/pricing": { title: "Pricing" },
  "/economics": { title: "Economics" },
};

export const DOCUMENT_TITLE_SUFFIX = "Platform 9S";

export function formatDocumentTitle(pageTitle: string): string {
  return `${pageTitle} - ${DOCUMENT_TITLE_SUFFIX}`;
}

const DETAIL_ROUTE_TITLES: ReadonlyArray<{ prefix: string; fallback: string }> = [
  { prefix: "/records/", fallback: "Record" },
  { prefix: "/drafts/", fallback: "Draft" },
  { prefix: "/tasks/", fallback: "Task" },
  { prefix: "/projects/", fallback: "Project" },
  { prefix: "/ai-audit/", fallback: "AI audit" },
  { prefix: "/admin/ledger/", fallback: "Ledger entry" },
  { prefix: "/finance/billing/", fallback: "Payment dossier" },
];

function resolveDetailRouteTitle(canonicalPath: string): RoutePageConfig | null {
  for (const detail of DETAIL_ROUTE_TITLES) {
    if (!canonicalPath.startsWith(detail.prefix)) continue;
    const segment = canonicalPath.slice(detail.prefix.length);
    if (!segment) continue;
    return { title: detail.fallback };
  }
  return null;
}

export function resolveDocumentTitle(pathname: string): string {
  const { title } = getRouteConfig(pathname);
  return formatDocumentTitle(title);
}

export interface BreadcrumbItem {
  label: string;
  path: string;
}

const PARENT_LABELS: Record<string, string> = {
  ai: "AI Platform",
  knowledge: "Knowledge",
  rag: "Knowledge",
  analytics: "Analytics",
  intake: "Intake Core",
  iam: "IAM",
  hrm: "Nhân sự",
  finance: "Tài chính",
};

function buildBreadcrumbs(path: string, title: string): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = [{ label: "Home", path: "/" }];
  const financeInvoiceDetailPrefix = "/finance/invoices/";
  const financePayableDetailPrefix = "/finance/payables/";
  const financeBankDetailPrefix = "/finance/bank/";
  if (path.startsWith(financeInvoiceDetailPrefix) && path !== ROUTES.FINANCE_INVOICES) {
    crumbs.push({ label: "Tài chính", path: ROUTES.FINANCE });
    crumbs.push({ label: "Phải thu", path: ROUTES.FINANCE_INVOICES });
    crumbs.push({ label: "Chi tiết phải thu", path });
    return crumbs;
  }
  if (path.startsWith(financePayableDetailPrefix) && path !== ROUTES.FINANCE_PAYABLES) {
    crumbs.push({ label: "Tài chính", path: ROUTES.FINANCE });
    crumbs.push({ label: "Phải trả", path: ROUTES.FINANCE_PAYABLES });
    crumbs.push({ label: "Chi tiết phải trả", path });
    return crumbs;
  }
  if (path.startsWith(financeBankDetailPrefix) && path !== ROUTES.FINANCE_BANK) {
    crumbs.push({ label: "Tài chính", path: ROUTES.FINANCE });
    crumbs.push({ label: "Sao kê ngân hàng", path: ROUTES.FINANCE_BANK });
    crumbs.push({ label: title, path });
    return crumbs;
  }
  const segments = path.split("/").filter(Boolean);

  if (segments.length === 0) return crumbs;

  if (segments[0] === "knowledge" && segments.length > 1) {
    crumbs.push({ label: PARENT_LABELS.knowledge, path: ROUTES.KNOWLEDGE_PLATFORM });
    for (let index = 1; index < segments.length - 1; index += 1) {
      const partialPath = `/knowledge/${segments.slice(1, index + 1).join("/")}`;
      const segmentRoute = ROUTE_CONFIG[partialPath];
      if (segmentRoute) {
        crumbs.push({ label: segmentRoute.title, path: partialPath });
      }
    }
    crumbs.push({ label: title, path });
    return crumbs;
  }

  if (segments.length === 1) {
    crumbs.push({ label: title, path });
    return crumbs;
  }

  const parentPath = `/${segments[0]}`;
  const parentRoute = ROUTE_CONFIG[parentPath];
  const parentLabel = parentRoute?.title ?? PARENT_LABELS[segments[0]] ?? segments[0];
  crumbs.push({ label: parentLabel, path: parentPath });
  crumbs.push({ label: title, path });
  return crumbs;
}

export function getRouteConfig(path: string): {
  title: string;
  description?: string;
  breadcrumbs: BreadcrumbItem[];
} {
  let canonicalPath = normalizeLegacyRoutePath(path);
  const tenantDetailPrefix = "/iam/tenants/";
  const customerDetailPrefix = "/parties/";
  const supplierDetailPrefix = `${ROUTES.SUPPLIER_DIRECTORY}/`;
  const partnerDetailPrefix = `${ROUTES.PARTNER_DIRECTORY}/`;
  const contractTemplateDetailPrefix = "/contracts/templates/";
  const contractsDetailPrefix = "/contracts/";
  const financeInvoiceDetailPrefix = "/finance/invoices/";
  const financePayableDetailPrefix = "/finance/payables/";
  const financeBankDetailPrefix = "/finance/bank/";
  const preRagReviewDetailPrefix = `${ROUTES.KNOWLEDGE_PRE_RAG_REVIEW}/`;
  const contentLibraryItemPrefix = `${ROUTES.KNOWLEDGE_CONTENT_LIBRARY}/`;
  const exact = canonicalPath.startsWith(tenantDetailPrefix)
    ? {
        title: "Tenant Detail",
        description: "",
      }
    : canonicalPath === ROUTES.SUPPLIER_DIRECTORY
      ? {
          title: "Suppliers",
          description: "Supplier party directory and commercial master data.",
        }
    : canonicalPath.startsWith(supplierDetailPrefix)
      ? {
          title: "Supplier Workspace",
          description: "Supplier profile, identity, commercial context, and governance evidence.",
        }
    : canonicalPath === ROUTES.PARTNER_DIRECTORY
      ? {
          title: "Partners",
          description: "Partner party directory and commercial master data.",
        }
    : canonicalPath.startsWith(partnerDetailPrefix)
      ? {
          title: "Partner Workspace",
          description: "Partner profile, identity, commercial context, and governance evidence.",
        }
    : canonicalPath === ROUTES.PARTY_IDENTITY_REVIEW || canonicalPath.startsWith(`${ROUTES.PARTY_IDENTITY_REVIEW}/`)
      ? {
          title: "Identity review",
          description: "Unresolved bindings and identity review under Customer Ops.",
        }
      : canonicalPath.startsWith(customerDetailPrefix) && canonicalPath !== ROUTES.PARTIES
      ? {
          title: "Customer Workspace",
          description: "Customer Ops workspace: overview, conversations, commercial projection, and evidence-backed brief.",
        }
      : canonicalPath.startsWith(contractTemplateDetailPrefix)
        ? {
            title: "Mẫu hợp đồng",
            description: "Template detail.",
          }
        : canonicalPath.startsWith(contractsDetailPrefix) && canonicalPath !== "/contracts" && canonicalPath !== "/contracts/templates"
          ? {
              title: "Hợp đồng",
              description: "Agreement detail.",
            }
          : canonicalPath.startsWith(financeInvoiceDetailPrefix)
            ? {
                title: "Chi tiết phải thu",
                description: "Receivable invoice detail and collection actions.",
              }
            : canonicalPath.startsWith(financePayableDetailPrefix)
              ? {
                  title: "Chi tiết phải trả",
                  description: "Payable detail and disbursement request before bank settlement.",
                }
              : canonicalPath.startsWith(financeBankDetailPrefix) &&
                  canonicalPath !== ROUTES.FINANCE_BANK_TRANSACTIONS
                ? {
                    title: "Tài khoản ngân hàng",
                    description: "Bank account statement import, transactions, allocation, and controls.",
                  }
                : canonicalPath.startsWith(preRagReviewDetailPrefix) && canonicalPath !== ROUTES.KNOWLEDGE_PRE_RAG_REVIEW
                  ? {
                      title: "Pre-RAG Review Detail",
                      description: "Full staging review for one candidate before searchable RAG indexing.",
                    }
                  : canonicalPath.startsWith(contentLibraryItemPrefix) && canonicalPath !== ROUTES.KNOWLEDGE_CONTENT_LIBRARY
                    ? {
                        title: "Content detail",
                        description: "Full-width content item review with extraction and storage refs.",
                      }
                    : (ROUTE_CONFIG[canonicalPath] ?? resolveDetailRouteTitle(canonicalPath));
  const title = exact?.title ?? "Nice Solutions Platform";
  const description = exact?.description;
  const breadcrumbs = buildBreadcrumbs(canonicalPath, title);
  return { title, description, breadcrumbs };
}
